/**
 * Catálogo de cards do Painel Comercial (Painel configurável — Fase 2).
 *
 * Espelha o `_widgets.jsx` do Dashboard Financeiro: torna o painel data-driven.
 * Cada entrada é `{ id, label, categoria, span_default, render }`:
 *   - `categoria`: 'kpi' (card pequeno, span 3 por default) ou 'faixa' (bloco
 *     largo — funil/perdas/ranking, span 12 por default).
 *   - `render(d, ctx)` recebe o PAYLOAD do painel (`d`) + um `ctx` com os dados
 *     já derivados em index.jsx (ex/cab/funil/ranking/perdas/subPeriodo/animacoes)
 *     e devolve o JSX do card — EXATAMENTE o display + fallbacks de hoje.
 *
 * O `index.jsx` monta o "chrome" (head + drag handle + ações de editar) ao redor
 * do `render`; aqui mora só o conteúdo. Os ids dos 8 KPIs + 3 faixas viram a
 * ordem default do layout (nada oculto) quando `layout_painel` está vazio —
 * garantindo regressão-zero com o painel atual.
 *
 * FASE 4 — cards novos (`valor_por_etapa`, `valor_ponderado`, `comissao_prevista`,
 * `leads_sem_orcamento`) entram no catálogo marcados com `oculto_default: true`:
 * NÃO aparecem no painel sem config (regressão-zero — segue mostrando só os 11
 * cards atuais), mas ficam disponíveis em "Adicionar card" no modo personalizar.
 * `layoutDefault()` exclui esses ids da ordem default e `ocultoDefaultIds()`
 * expõe o conjunto p/ o index.jsx semeá-los em `ocultos` por padrão.
 */
import React, { createContext, useContext, useState } from "react";
import { motion } from "framer-motion";

import ExplicacaoCard from "../../../components/ExplicacaoCard";
import { useThemeToggle } from "../../../context/ThemeContext";
import { fmtMoeda, fmtNumero } from "../../Orcamentos/helpers";
import { EmptyMsg } from "../../Orcamentos/styles";

// --- Animacoes (espelham index.jsx — feel do Dashboard Financeiro) -----------
const EASE_ENTRADA = [0.16, 1, 0.3, 1];
// Transition das barras que "crescem" (funil, ranking, meta, razoes).
const trBarra = (delay = 0.15) => ({ duration: 0.7, ease: EASE_ENTRADA, delay });

// --- Formatacao --------------------------------------------------------------

// Abrevia numeros grandes: 1.840.000 -> "1.84M", 67000 -> "67k".
export function fmtCompact(valor) {
  const n = Number(valor);
  if (valor === null || valor === undefined || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  }
  if (abs >= 1_000) {
    return `${Math.round(n / 1_000)}k`;
  }
  return fmtNumero(n, 0);
}

// --- Formato configuravel por card (Fase 3) ----------------------------------
// O card declara `ajustes.formato:true`; o gestor escolhe em
// layout_painel.formatos[id] = { tipo:'moeda'|'moeda_compacta'|'percentual',
// casas:0..4 }. O render le ctx.opcoesCard.formato e aplica AQUI. Sem config =
// usa o `formatoPadrao` que cada card passa (regressao-zero da Fase 2).
export const FORMATOS_VALOR = [
  { value: "moeda", label: "Moeda (R$)" },
  { value: "moeda_compacta", label: "Moeda compacta (R$ 1,8M)" },
  { value: "percentual", label: "Percentual (%)" },
];

// Formata um numero conforme {tipo,casas}. `tipo` ausente cai no padrao do card.
export function fmtValor(valor, formato, formatoPadrao = "moeda_compacta") {
  const tipo = formato?.tipo || formatoPadrao;
  const casas = Number.isFinite(Number(formato?.casas))
    ? Math.max(0, Math.min(4, Number(formato.casas)))
    : undefined;
  if (valor === null || valor === undefined || valor === "") return "—";
  if (tipo === "moeda") return fmtMoeda(valor);
  if (tipo === "percentual") return `${fmtNumero(valor, casas ?? 0)}%`;
  // moeda_compacta (default): compacta com casas opcionais de override.
  if (casas != null) {
    const n = Number(valor);
    if (Number.isNaN(n)) return "—";
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(casas)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(casas)}k`;
    return fmtNumero(n, casas);
  }
  return fmtCompact(valor);
}

// Tom do delta -> classe (cor so comunica estado).
const TONE_TO_DELTA_CLASS = { up: "up", down: "down", flat: "flat" };
export const ARROW = { up: "▲", down: "▼", flat: "—" };

// Decide o tom de um delta numerico simples (sobe=up, cai=down, zero=flat).
function deltaToneFromNumber(v) {
  if (v === null || v === undefined || Number(v) === 0) return "flat";
  return Number(v) > 0 ? "up" : "down";
}

// Gradientes deterministicos por indice do ranking.
const RANKING_GRADIENTS = [
  "linear-gradient(135deg,var(--chart-1),var(--chart-3))",
  "linear-gradient(135deg,var(--chart-2),var(--chart-7))",
  "linear-gradient(135deg,var(--chart-4),var(--chart-5))",
  "linear-gradient(135deg,var(--chart-6),var(--chart-8))",
];

function iniciais(nome) {
  const p = (nome || "").trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
}

// Delta do ranking formatado de forma legivel.
export function formatRankingDelta(r) {
  if (!Number(r?.ganhos)) return { tone: "flat", texto: "—" };
  if (r.delta_pct == null) return { tone: "flat", texto: "novo" };
  const n = Math.round(Number(r.delta_pct));
  if (n === 0) return { tone: "flat", texto: "0%" };
  const tone = n > 0 ? "up" : "down";
  const abs = Math.abs(n);
  return { tone, texto: abs >= 500 ? (tone === "up" ? "alta" : "queda") : `${abs}%` };
}

// --- Grafico configuravel por card (Fase 3) ----------------------------------
// O card declara `ajustes.grafico:true`; o gestor escolhe em
// layout_painel.charts[id] = { tipo:'line'|'bar'|'area', paleta:1..8 }. O render
// le ctx.opcoesCard.grafico e passa pra <Spark>. Sem config = line + a paleta
// default do card (`chart`), preservando o visual da Fase 2.
export const TIPOS_GRAFICO = [
  { value: "line", label: "Linha" },
  { value: "bar", label: "Barras" },
  { value: "area", label: "Área" },
];
// 8 paletas do design system (var(--chart-1..8)).
export const PALETAS_GRAFICO = [1, 2, 3, 4, 5, 6, 7, 8];

// --- Sparkline inline (line/bar/area conforme `tipo`) ------------------------
function Spark({ pontos, chart = 1, tipo = "line" }) {
  const { animacoes = true } = useThemeToggle() || {};
  if (!Array.isArray(pontos) || pontos.length < 2) return null;
  const W = 180;
  const H = 36;
  const PAD = 4;
  const vals = pontos.map((p) => Number(p) || 0);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const cor = `var(--chart-${chart})`;
  const stepX = (W - PAD * 2) / (vals.length - 1);
  const toX = (i) => PAD + i * stepX;
  const toY = (v) => H - PAD - ((v - min) / span) * (H - PAD * 2);

  // Barras: uma coluna por ponto, crescendo da base.
  if (tipo === "bar") {
    const n = vals.length;
    const gap = 2;
    const bw = Math.max(1, (W - PAD * 2 - gap * (n - 1)) / n);
    return (
      <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        {vals.map((v, i) => {
          const x = PAD + i * (bw + gap);
          const y = toY(v);
          const h = Math.max(1, H - PAD - y);
          return (
            <motion.rect
              key={i}
              x={x.toFixed(1)}
              width={bw.toFixed(1)}
              rx="1"
              fill={cor}
              initial={animacoes ? { height: 0, y: H - PAD } : false}
              animate={{ height: h.toFixed(1), y: y.toFixed(1) }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 + i * 0.03 }}
            />
          );
        })}
      </svg>
    );
  }

  const linha = vals
    .map((v, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)} ${toY(v).toFixed(1)}`)
    .join(" ");

  // Area: mesma curva, mas fechada ate a base e preenchida com leve opacidade.
  const areaPath =
    tipo === "area"
      ? `${linha} L${toX(vals.length - 1).toFixed(1)} ${(H - PAD).toFixed(1)} L${toX(0).toFixed(1)} ${(H - PAD).toFixed(1)} Z`
      : null;

  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      {areaPath ? (
        <motion.path
          d={areaPath}
          fill={cor}
          stroke="none"
          initial={animacoes ? { opacity: 0 } : false}
          animate={{ opacity: 0.18 }}
          transition={{ duration: 0.8, ease: "easeInOut", delay: 0.2 }}
        />
      ) : null}
      <motion.path
        d={linha}
        stroke={cor}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        initial={animacoes ? { pathLength: 0, opacity: 0 } : false}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeInOut", delay: 0.2 }}
      />
    </svg>
  );
}

// --- Explicacao do card ("?" + regua) ------------------------------------------
// O index.jsx prove, por card, o titulo e a explicacao ja resolvida para a fonte
// em uso (explicacoes.js). Via contexto para os renders nao mudarem de assinatura.
export const ExplicacaoDoCardContext = createContext(null);

function AjudaDoCard() {
  const ctx = useContext(ExplicacaoDoCardContext);
  if (!ctx?.explicacao) return null;
  return <ExplicacaoCard titulo={ctx.titulo} explicacao={ctx.explicacao} />;
}

function ReguaDoCard({ className = "" }) {
  const regua = useContext(ExplicacaoDoCardContext)?.explicacao?.regua;
  if (!regua) return null;
  return <div className={`explicacao-regua ${className}`.trim()}>{regua}</div>;
}

// Cabecalho das faixas: titulo + regua a esquerda; resumo + "?" a direita.
function CabecalhoFaixa({ titulo, children }) {
  return (
    <div className="block__head">
      <div style={{ minWidth: 0 }}>
        <div className="block__title">{titulo}</div>
        <ReguaDoCard className="explicacao-regua--faixa" />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {children}
        <AjudaDoCard />
      </div>
    </div>
  );
}

// --- Cards que so leem orcamento feito no sistema (ADR-0079) -----------------
// Sem nenhum orcamento no sistema o card mostraria 0 como se fosse resultado.
// `escopo`: "periodo" (cards do periodo) ou "total" (cards do momento atual).
function semOrcamentos(d, escopo) {
  const flags = d?.orcamentos_no_sistema;
  return Boolean(flags) && flags[escopo] === false;
}

function AvisoSemOrcamentos({ escopo }) {
  return (
    <div className="tiny muted" style={{ marginTop: 4 }}>
      {escopo === "total"
        ? "Depende de orçamentos feitos no sistema — a unidade não tem nenhum."
        : "Depende de orçamentos feitos no sistema — nenhum no período."}
    </div>
  );
}

// --- KPI widget (conteudo interno: head + valor + delta + children) ----------
// O chrome (.widget / drag / acoes) e montado pelo index; aqui so o conteudo.
function KpiWidget({ titulo, meta, valor, unidade, delta, children }) {
  return (
    <>
      <div className="widget__head">
        <div className="widget__title">{titulo}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {meta ? <span className="widget__meta">{meta}</span> : null}
          <AjudaDoCard />
        </div>
      </div>
      <ReguaDoCard />
      <div className="val">
        {valor}
        {unidade ? <span className="unit">{unidade}</span> : null}
      </div>
      {delta ? (
        <div className={`delta ${TONE_TO_DELTA_CLASS[delta.tone] || "flat"}`}>
          {ARROW[delta.tone] || "—"} {delta.texto}
        </div>
      ) : null}
      {children}
    </>
  );
}

// --- Barra de progresso da meta (KPI com alvo) -------------------------------
// A meta e UMA so (Comercial > Configuracoes) e o backend ja devolve o % sobre
// a meta do periodo (proporcional: semana/trimestre/ano). ADR-0079.
function MetaBar({ cab }) {
  const { animacoes = true } = useThemeToggle() || {};
  const meta = Number(cab?.meta) || 0;
  const pct = Number(cab?.pct_meta) || 0;
  const p = Math.max(0, Math.min(100, pct));
  if (meta <= 0) {
    return (
      <div className="tiny muted" style={{ marginTop: 6 }}>
        Sem meta — defina em Comercial › Configurações.
      </div>
    );
  }
  return (
    <>
      <div
        style={{
          height: 6,
          background: "var(--surface-2)",
          borderRadius: 999,
          overflow: "hidden",
          marginTop: 6,
        }}
      >
        <motion.div
          style={{
            height: "100%",
            background: "var(--accent)",
            borderRadius: 999,
          }}
          initial={animacoes ? { width: 0 } : false}
          animate={{ width: `${p}%` }}
          transition={trBarra(0.15)}
        />
      </div>
      <div className="tiny muted" style={{ marginTop: 4 }}>
        {fmtNumero(pct, 0)}% da {cab?.meta_rotulo || "meta"} (R$ {fmtCompact(meta)})
      </div>
    </>
  );
}

// --- Funil (barras horizontais legiveis) ------------------------------------
// Sem dados o funil fica vazio — antes desenhava numeros de exemplo (412
// leads...) que pareciam reais. Numero de barras = etapas do CRM da unidade.

// Opacidade decrescente por nivel (o ultimo usa cor cheia de sucesso).
const FUNIL_OPACIDADES = [1, 0.82, 0.64, 0.46];

function FunilBars({ niveis }) {
  const { animacoes = true } = useThemeToggle() || {};
  const linhas = Array.isArray(niveis) ? niveis : [];

  return (
    <div className="funil-bars">
      {linhas.map((n, i) => {
        const ultimo = i === linhas.length - 1;
        const pct = Math.max(0, Math.min(100, Number(n.pct) || 0));
        return (
          <div className="funil-row" key={`${n.label}-${i}`}>
            <div className="funil-row__head">
              <span className="funil-row__label">{n.label}</span>
              <span className="funil-row__val">
                <strong>{fmtNumero(n.count, 0)}</strong>
                {" · "}
                {fmtNumero(pct, pct < 10 ? 1 : 0)}%
              </span>
            </div>
            <div className="funil-track">
              <motion.span
                className="funil-fill"
                style={{
                  background: ultimo
                    ? "var(--success-solid)"
                    : "var(--chart-1)",
                  opacity: ultimo ? 1 : FUNIL_OPACIDADES[Math.min(i, 3)],
                }}
                initial={animacoes ? { width: 0 } : false}
                animate={{ width: `${Math.max(pct, 4)}%` }}
                transition={trBarra(0.12 + i * 0.06)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- Valor por etapa (faixa F4): barras horizontais por etapa do pipeline -----
// Cada etapa do CRM tem cor de dominio (`etapa_cor`, hex) — cor que "comunica
// estado" (a etapa), portanto permitida na barra. Larguras proporcionais ao
// MAIOR valor da lista (a etapa lider enche a barra). Empty-state quando nao ha
// lead aberto. `valores` = d.valor_por_etapa (ja ordenado por etapa.ordem asc).
function EtapaBars({ etapas }) {
  const { animacoes = true } = useThemeToggle() || {};
  const linhas = Array.isArray(etapas) ? etapas : [];
  if (linhas.length === 0) {
    return (
      <EmptyMsg style={{ padding: 8 }}>Nenhum lead aberto no pipeline.</EmptyMsg>
    );
  }
  const maxValor = Math.max(1, ...linhas.map((e) => Number(e.valor) || 0));
  return (
    <div className="funil-bars">
      {linhas.map((e, i) => {
        const valor = Number(e.valor) || 0;
        const pct = Math.max(0, Math.min(100, (valor / maxValor) * 100));
        const cor = e.etapa_cor || "var(--chart-1)";
        return (
          <div className="funil-row" key={e.etapa_id ?? `${e.etapa_nome}-${i}`}>
            <div className="funil-row__head">
              <span className="funil-row__label">{e.etapa_nome || "—"}</span>
              <span className="funil-row__val">
                <strong>{fmtMoeda(valor)}</strong>
                {" · "}
                {fmtNumero(e.qtd, 0)} lead{Number(e.qtd) === 1 ? "" : "s"}
              </span>
            </div>
            <div className="funil-track">
              <motion.span
                className="funil-fill"
                style={{ background: cor }}
                initial={animacoes ? { width: 0 } : false}
                animate={{ width: `${Math.max(pct, 4)}%` }}
                transition={trBarra(0.12 + i * 0.05)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// CATALOGO — cada card como dado. `render(d, ctx)` devolve o conteudo interno.
//
// ctx fornecido pelo index.jsx:
//   { ex, cab, funil, ranking, perdas, totalAnalises, subPeriodo, periodo,
//     PERIODO_LABEL, animacoes }
// =============================================================================

// ---- KPIs (8) ----

function renderPipeline(d, { opcoesCard }) {
  // Sem seta e sem linha: o numero e o que esta aberto AGORA e o sistema nao
  // guarda o historico do pipeline para comparar (ADR-0079).
  const op = opcoesCard || {};
  const qtd = d?.pipeline?.qtd;
  return (
    <KpiWidget
      titulo="Pipeline aberto"
      meta="R$"
      valor={fmtValor(d?.pipeline?.soma, op.formato, "moeda_compacta")}
    >
      {qtd != null ? (
        <div className="tiny muted" style={{ marginTop: 4 }}>
          {fmtNumero(qtd, 0)} {d?.fontes?.pipeline === "orcamento" ? "orçamento" : "lead"}
          {Number(qtd) === 1 ? "" : "s"} em aberto
        </div>
      ) : null}
    </KpiWidget>
  );
}

// Linha "vs mês anterior" do card de Receita realizada (só no período Mês).
// É contexto/referência — renderizada em tom neutro (muted), sem cor de estado:
// no início do mês o valor sempre fica "menor" que o mês fechado anterior, e
// pintar isso de vermelho pareceria defeito (o oposto do que se quer). O sentido
// da variação vem da palavra (menor/maior/estável), não da cor. O % e o valor
// exibido referem o MESMO número: quanto o mês anterior fechou.
function ComparativoMesAnterior({ valor, deltaPct, formato }) {
  let variacao;
  if (deltaPct === null || deltaPct === undefined) {
    variacao = "sem base no mês anterior";
  } else if (Number(deltaPct) === 0) {
    variacao = "estável vs. mês anterior";
  } else {
    const palavra = Number(deltaPct) < 0 ? "menor" : "maior";
    variacao = `${Math.abs(Number(deltaPct))}% ${palavra} que o mês anterior`;
  }
  return (
    <div
      className="tiny muted"
      style={{ marginTop: 8, lineHeight: 1.3 }}
      title="Comparativo com o mês anterior fechado"
    >
      Mês anterior: {fmtValor(valor, formato, "moeda_compacta")}
      <span aria-hidden="true"> · </span>
      {variacao}
    </div>
  );
}

function renderReceitaRealizada(d, { ex, cab, opcoesCard, periodo }) {
  const op = opcoesCard || {};
  const valorBruto = cab?.receita_realizada ?? ex?.receita_realizada?.valor;
  const mesAnteriorValor = ex?.receita_realizada?.mes_anterior_valor;
  // "vs mês anterior" só faz sentido quando o card mostra o mês corrente.
  const mostrarMesAnterior = periodo === "mes" && mesAnteriorValor != null;
  return (
    <KpiWidget
      titulo="Receita realizada"
      meta="R$"
      valor={fmtValor(valorBruto, op.formato, "moeda_compacta")}
      delta={
        ex?.receita_realizada?.delta_pct != null
          ? {
              tone: deltaToneFromNumber(ex.receita_realizada.delta_pct),
              texto: `${Math.abs(ex.receita_realizada.delta_pct)}%${
                (cab?.pct_meta ?? ex?.receita_realizada?.pct_meta) != null
                  ? ` · ${fmtNumero(cab?.pct_meta ?? ex.receita_realizada.pct_meta, 0)}% meta`
                  : ""
              }`,
            }
          : null
      }
    >
      <MetaBar cab={cab} />
      {mostrarMesAnterior ? (
        <ComparativoMesAnterior
          valor={mesAnteriorValor}
          deltaPct={ex?.receita_realizada?.mes_anterior_delta_pct}
          formato={op.formato}
        />
      ) : null}
    </KpiWidget>
  );
}

function renderTaxaConversao(d, { ex, opcoesCard }) {
  const op = opcoesCard || {};
  return (
    <KpiWidget
      titulo="Taxa conversão"
      meta="%"
      valor={fmtNumero(d?.taxa_conversao?.taxa, 0)}
      unidade="%"
      delta={
        ex?.taxa_conversao?.delta_pt != null
          ? {
              tone: deltaToneFromNumber(ex.taxa_conversao.delta_pt),
              texto: `${Math.abs(ex.taxa_conversao.delta_pt)}pt${
                ex?.taxa_conversao?.anterior != null
                  ? ` · vs ${fmtNumero(ex.taxa_conversao.anterior, 0)}% ant.`
                  : ""
              }`,
            }
          : null
      }
    >
      <Spark
        pontos={ex?.taxa_conversao?.spark}
        chart={op.grafico?.paleta || 2}
        tipo={op.grafico?.tipo || "line"}
      />
    </KpiWidget>
  );
}

function renderTicketMedio(d, { ex, opcoesCard }) {
  const op = opcoesCard || {};
  return (
    <KpiWidget
      titulo="Ticket médio"
      meta="R$"
      valor={fmtValor(d?.ticket_medio?.media, op.formato, "moeda_compacta")}
      delta={
        ex?.ticket_medio?.delta_pct != null
          ? {
              tone: deltaToneFromNumber(ex.ticket_medio.delta_pct),
              texto:
                Number(ex.ticket_medio.delta_pct) === 0
                  ? "flat"
                  : `${Math.abs(ex.ticket_medio.delta_pct)}%`,
            }
          : null
      }
    >
      <Spark
        pontos={ex?.ticket_medio?.spark}
        chart={op.grafico?.paleta || 4}
        tipo={op.grafico?.tipo || "line"}
      />
    </KpiWidget>
  );
}

function renderTempoResposta(d, { ex }) {
  return (
    <KpiWidget
      titulo="Tempo resposta"
      meta="h"
      valor={fmtNumero(ex?.tempo_resposta?.horas, 1)}
      unidade={ex?.tempo_resposta?.horas != null ? "h" : null}
      delta={
        ex?.tempo_resposta?.delta_horas != null
          ? {
              // menos tempo = melhor -> delta negativo conta como "up".
              tone:
                Number(ex.tempo_resposta.delta_horas) === 0
                  ? "flat"
                  : Number(ex.tempo_resposta.delta_horas) < 0
                    ? "up"
                    : "down",
              texto: `${Math.abs(ex.tempo_resposta.delta_horas)}h`,
            }
          : null
      }
    >
      <div className="tiny muted" style={{ marginTop: 4 }}>
        {ex?.tempo_resposta?.horas == null
          ? "nenhum lead atendido no período"
          : ""}
        {ex?.tempo_resposta?.alvo_horas != null
          ? `${ex?.tempo_resposta?.horas == null ? " · " : ""}alvo ${fmtNumero(ex.tempo_resposta.alvo_horas, 1)} h`
          : ""}
      </div>
    </KpiWidget>
  );
}

function renderTempoMedioAprovacao(d, { ex }) {
  const dias = d?.tempo_medio_aprovacao?.dias;
  const semOrc = semOrcamentos(d, "periodo");
  return (
    <KpiWidget
      titulo="Lead time orç→aprov"
      meta="dias"
      valor={fmtNumero(dias, 1)}
      unidade={dias != null ? "d" : null}
      delta={
        ex?.lead_time?.delta_dias != null
          ? {
              tone:
                Number(ex.lead_time.delta_dias) === 0
                  ? "flat"
                  : Number(ex.lead_time.delta_dias) < 0
                    ? "up"
                    : "down",
              texto: `${Math.abs(ex.lead_time.delta_dias)}d`,
            }
          : null
      }
    >
      {semOrc ? (
        <AvisoSemOrcamentos escopo="periodo" />
      ) : dias == null ? (
        <div className="tiny muted" style={{ marginTop: 4 }}>
          nenhuma aprovação no período
        </div>
      ) : null}
    </KpiWidget>
  );
}

function renderMargemMedia(d, { ex }) {
  const pct = ex?.margem_media?.pct;
  const semOrc = semOrcamentos(d, "periodo");
  return (
    <KpiWidget
      titulo="Margem média"
      meta="%"
      valor={fmtNumero(pct, 1)}
      unidade={pct != null ? "%" : null}
      delta={
        ex?.margem_media?.delta_pt != null
          ? {
              tone: deltaToneFromNumber(ex.margem_media.delta_pt),
              texto: `${Math.abs(ex.margem_media.delta_pt)}pt`,
            }
          : null
      }
    >
      {semOrc ? (
        <AvisoSemOrcamentos escopo="periodo" />
      ) : pct == null ? (
        <div className="tiny muted" style={{ marginTop: 4 }}>
          sem vendas no período
        </div>
      ) : null}
    </KpiWidget>
  );
}

function renderAprovacoesForaLimite(d, { ex }) {
  return (
    <KpiWidget
      titulo="Descontos acima do limite"
      meta="qtd"
      valor={
        semOrcamentos(d, "periodo")
          ? "—"
          : fmtNumero(ex?.aprovacoes_fora_limite?.qtd, 0)
      }
      delta={
        ex?.aprovacoes_fora_limite?.delta_n != null && !semOrcamentos(d, "periodo")
          ? {
              // mais aprovacoes fora do limite = pior -> aumento conta
              // como "down" (tom danger). Texto pede revisao.
              tone:
                Number(ex.aprovacoes_fora_limite.delta_n) === 0
                  ? "flat"
                  : Number(ex.aprovacoes_fora_limite.delta_n) > 0
                    ? "down"
                    : "up",
              texto: `${Math.abs(ex.aprovacoes_fora_limite.delta_n)} · revisar`,
            }
          : null
      }
    >
      {semOrcamentos(d, "periodo") ? <AvisoSemOrcamentos escopo="periodo" /> : null}
    </KpiWidget>
  );
}

// ---- KPIs novos (Fase 4 — oculto_default) ----

// Valor ponderado: Σ valor_total × probabilidade/100 dos orçamentos ativos.
// A fonte (pipeline) afeta o valor-por-lead no backend; o seletor de fonte
// reusa o mesmo do pipeline (ajustes.fonte:'pipeline'). Formato configuravel.
function renderValorPonderado(d, { opcoesCard }) {
  const op = opcoesCard || {};
  const vp = d?.valor_ponderado || {};
  return (
    <KpiWidget
      titulo="Valor ponderado"
      meta="R$"
      valor={semOrcamentos(d, "total") ? "—" : fmtValor(vp.soma, op.formato, "moeda_compacta")}
    >
      {semOrcamentos(d, "total") ? (
        <AvisoSemOrcamentos escopo="total" />
      ) : (
        <div className="tiny muted" style={{ marginTop: 4 }}>
          ponderado por probabilidade
          {vp.qtd != null ? ` · ${fmtNumero(vp.qtd, 0)} orç.` : ""}
        </div>
      )}
    </KpiWidget>
  );
}

// Comissao prevista: soma de comissao_prevista da performance_vendedor (ja no
// payload desde F1 — F4 so expoe como card). Formato configuravel.
function renderComissaoPrevista(d, { opcoesCard }) {
  const op = opcoesCard || {};
  return (
    <KpiWidget
      titulo="Comissão prevista"
      meta="R$"
      valor={
        semOrcamentos(d, "periodo")
          ? "—"
          : fmtValor(d?.comissao_prevista_total, op.formato, "moeda")
      }
    >
      {semOrcamentos(d, "periodo") ? (
        <AvisoSemOrcamentos escopo="periodo" />
      ) : (
        <div className="tiny muted" style={{ marginTop: 4 }}>
          vendas ganhas no período
        </div>
      )}
    </KpiWidget>
  );
}

// Leads sem orcamento: contagem de leads abertos sem nenhum Orcamento vinculado.
function renderLeadsSemOrcamento(d) {
  const qtd = d?.leads_sem_orcamento?.qtd;
  return (
    <KpiWidget
      titulo="Leads sem orçamento"
      meta="qtd"
      valor={semOrcamentos(d, "total") ? "—" : fmtNumero(qtd, 0)}
    >
      {semOrcamentos(d, "total") ? (
        <AvisoSemOrcamentos escopo="total" />
      ) : (
        <div className="tiny muted" style={{ marginTop: 4 }}>
          leads abertos sem nenhuma proposta
        </div>
      )}
    </KpiWidget>
  );
}

// ---- Faixas (3 + 1 nova F4) ----

// Valor por etapa (faixa F4 — oculto_default): barras horizontais por etapa do
// pipeline, cor de dominio (etapa_cor). Span largo. Lead-centrico (Σ aqui pode
// nao bater com pipeline.soma quando a fonte e "orcamento" — recortes diferentes
// (etapa do lead vs situacao do orcamento), conforme spec do backend F4).
function renderValorPorEtapa(d) {
  const etapas = d?.valor_por_etapa || [];
  return (
    <div className="block">
      <CabecalhoFaixa titulo="Valor por etapa">
        <span className="tiny mono muted">PIPELINE ABERTO</span>
      </CabecalhoFaixa>
      <div className="block__body">
        <EtapaBars etapas={etapas} />
      </div>
    </div>
  );
}

// Funil comercial (block com head proprio + funil-layout). O index NAO injeta
// head para faixas (categoria 'faixa' renderiza o block inteiro aqui), para
// preservar os heads especificos de cada faixa.
function renderFunil(d, { funil, periodo, PERIODO_LABEL }) {
  const porOrcamento = funil?.fonte === "orcamento";
  const niveis = funil?.niveis || [];
  const vazio = !Number(funil?.total);
  const unidadeFunil = porOrcamento ? "dos orçamentos" : "dos leads";
  return (
    <div className="block">
      <CabecalhoFaixa
        titulo={`Funil comercial · ${PERIODO_LABEL[periodo] || "período"}`}
      >
        <span className="tiny mono muted">
          {!vazio && funil?.conv_total_pct != null
            ? `${fmtNumero(funil.conv_total_pct, 1)}% chegam ao fim`
            : porOrcamento
              ? "orçamentos"
              : "etapas do CRM"}
        </span>
      </CabecalhoFaixa>
      <div className="block__body">
        {vazio ? (
          <EmptyMsg style={{ padding: 8 }}>
            {porOrcamento
              ? "Nenhum orçamento criado no período."
              : "Nenhum lead chegou no período."}
          </EmptyMsg>
        ) : (
          <div className="funil-layout">
            <FunilBars niveis={niveis} />
            <div className="stack" style={{ gap: 10 }}>
              <div className="funil-stat">
                <div className="tiny mono muted">PERDIDOS</div>
                <div className="big" style={{ color: "var(--danger-text)" }}>
                  {fmtNumero(funil?.caidos, 0)}
                </div>
                <div className="tiny muted">
                  {`${fmtNumero(funil?.pct_caidos, 1)}% ${unidadeFunil}`}
                </div>
              </div>
              <div className="funil-stat">
                <div className="tiny mono muted">MAIOR PERDA</div>
                <div className="lbl">{funil?.maior_perda?.label || "—"}</div>
                <div className="tiny muted">
                  {funil?.maior_perda?.perdas
                    ? `${fmtNumero(funil.maior_perda.perdas, 0)} não passaram`
                    : "—"}
                </div>
              </div>
              <div className="funil-stat">
                <div className="tiny mono muted">MELHOR PASSAGEM</div>
                <div className="lbl" style={{ color: "var(--success-text)" }}>
                  {funil?.melhor_etapa?.label || "—"}
                </div>
                <div className="tiny muted">
                  {funil?.melhor_etapa?.pct
                    ? `${fmtNumero(funil.melhor_etapa.pct, 0)}% passaram`
                    : "—"}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Por que perdemos (block com reasons).
// ---- Perdas (lead-centrico — oculto_default) ----

// Valor perdido: Σ Lead.valor_estimado dos leads perdidos no periodo.
function renderValorPerdido(d, { opcoesCard }) {
  const op = opcoesCard || {};
  const p = d?.perdas || {};
  const qtd = Number(p.qtd) || 0;
  return (
    <KpiWidget
      titulo="Valor perdido"
      meta="R$"
      valor={fmtValor(p.valor_perdido, op.formato, "moeda_compacta")}
    >
      <div className="tiny muted" style={{ marginTop: 4 }}>
        {fmtNumero(qtd, 0)} lead{qtd === 1 ? "" : "s"} perdido{qtd === 1 ? "" : "s"}
      </div>
    </KpiWidget>
  );
}

// % de perda: perdidos / (ganhos + perdidos) no periodo.
function renderTaxaPerda(d) {
  const p = d?.perdas || {};
  const taxa = Number(p.taxa_perda) || 0;
  return (
    <KpiWidget titulo="% de perda" meta="%" valor={`${fmtNumero(taxa, 0)}%`}>
      <div className="tiny muted" style={{ marginTop: 4 }}>
        {fmtNumero(p.qtd, 0)} perdidos · {fmtNumero(p.ganhos, 0)} ganhos
      </div>
    </KpiWidget>
  );
}

// Motivos de perda (faixa): ranking por VALOR perdido — complementa o "Por que
// perdemos" (que ranqueia por contagem/pct). Barra vermelha (danger = estado de
// perda, paleta canonica).
const ORDENS_MOTIVOS = [
  { value: "valor", label: "Valor" },
  { value: "qtd", label: "Qtd" },
];

// Componente (e nao funcao de render) porque guarda o seletor em estado.
// O seletor nao e salvo: cada pessoa alterna na hora; ao recarregar volta a
// ordenar por valor (ADR-0079).
function MotivosPerda({ d, animacoes }) {
  const [ordem, setOrdem] = useState("valor");
  const motivos = d?.perdas?.motivos || [];
  // A fonte "orcamento" nao traz valor por motivo — so da para ordenar por qtd.
  const temValor = motivos.some((m) => (Number(m.valor) || 0) > 0);
  const porValor = ordem === "valor" && temValor;
  const base = (m) => (porValor ? Number(m.valor) || 0 : Number(m.count) || 0);
  const lista = [...motivos].sort((a, b) => base(b) - base(a)).slice(0, 8);
  const maxBase = Math.max(1, ...lista.map(base));
  return (
    <div className="block">
      <CabecalhoFaixa titulo="Motivos de perda">
        <div className="tabchip-group" role="radiogroup" aria-label="Ordenar motivos por">
          {ORDENS_MOTIVOS.map((o) => {
            const ativo = (o.value === "valor") === porValor;
            const indisponivel = o.value === "valor" && !temValor;
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={ativo}
                disabled={indisponivel}
                title={indisponivel ? "Esta fonte não traz valor por motivo" : undefined}
                className={`tabchip${ativo ? " active" : ""}`}
                onClick={() => setOrdem(o.value)}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </CabecalhoFaixa>
      <div className="block__body">
        {lista.length === 0 ? (
          <EmptyMsg style={{ padding: 8 }}>Nenhuma perda no período.</EmptyMsg>
        ) : (
          <div className="ranking">
            {lista.map((m, i) => {
              const barPct = Math.max(2, (base(m) / maxBase) * 100);
              return (
                <div key={m.codigo ?? `${m.label}-${i}`} className="ranking__row">
                  <span className="ranking__pos">{i + 1}</span>
                  <div className="ranking__meta">
                    <div className="nm">{m.label}</div>
                    <div className="sub">
                      {fmtNumero(m.count, 0)} lead{m.count === 1 ? "" : "s"} ·{" "}
                      {fmtNumero(m.pct, 0)}%
                    </div>
                  </div>
                  <div className="ranking__bar" aria-hidden="true">
                    <motion.span
                      style={{ background: "var(--danger)" }}
                      initial={animacoes ? { width: 0 } : false}
                      animate={{ width: `${barPct}%` }}
                      transition={trBarra(0.1 + i * 0.05)}
                    />
                  </div>
                  <span className="ranking__val">
                    {porValor ? `R$ ${fmtCompact(m.valor)}` : fmtNumero(m.count, 0)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function renderMotivosPerda(d, { animacoes }) {
  return <MotivosPerda d={d} animacoes={animacoes} />;
}

function renderPorQuePerdemos(d, { perdas, totalAnalises, animacoes }) {
  // Resumo lead-centrico (qtd perdidos + valor perdido + taxa de perda) vem do
  // bloco `perdas` do payload; cai para `totalAnalises` quando ausente.
  const p = d?.perdas || {};
  const qtd = p.qtd ?? totalAnalises;
  return (
    <div className="block">
      <CabecalhoFaixa titulo="Por que perdemos">
        <span className="tiny mono muted">
          {fmtNumero(qtd, 0)} PERDIDOS
          {p.valor_perdido != null ? ` · R$ ${fmtCompact(p.valor_perdido)}` : ""}
          {p.taxa_perda != null ? ` · ${fmtNumero(p.taxa_perda, 0)}% PERDA` : ""}
        </span>
      </CabecalhoFaixa>
      <div className="block__body">
        {perdas.length === 0 ? (
          <EmptyMsg style={{ padding: 8 }}>
            Nenhuma razão de perda no período.
          </EmptyMsg>
        ) : (
          <div className="reasons" style={{ marginBottom: 18 }}>
            {perdas.map((r, i) => {
              const pct = Math.max(0, Math.min(100, Number(r.pct) || 0));
              const barTone =
                r.tom === "danger"
                  ? "danger"
                  : r.tom === "warn" || r.tom === "warning"
                    ? "warn"
                    : r.tom === "success"
                      ? "success"
                      : "";
              return (
                <React.Fragment key={r.codigo ?? `${r.label}-${i}`}>
                  <div className="reason">
                    <span className="reason__txt">{r.label}</span>
                    <span className="reason__pct">{fmtNumero(pct, 0)}%</span>
                  </div>
                  <div className="reason">
                    <div className={`reason__bar${barTone ? ` ${barTone}` : ""}`}>
                      <motion.span
                        className="f"
                        initial={animacoes ? { width: 0 } : false}
                        animate={{ width: `${pct}%` }}
                        transition={trBarra(0.1 + i * 0.04)}
                      />
                    </div>
                    <div />
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Top vendedores (block full-width com ranking).
function renderRankingVendedores(d, { ranking, subPeriodo, animacoes }) {
  return (
    <div className="block painel-ranking">
      <CabecalhoFaixa titulo="Top vendedores">
        <span className="tiny mono muted">{subPeriodo.toUpperCase()}</span>
      </CabecalhoFaixa>
      <div className="block__body">
        {ranking.length === 0 ? (
          <EmptyMsg style={{ padding: 8 }}>Nenhum vendedor no período.</EmptyMsg>
        ) : (
          <div className="ranking ranking--wide">
            {(() => {
              const maxValor = Math.max(
                1,
                ...ranking.map((r) => Number(r.valor_ganho) || 0)
              );
              return ranking.map((r, i) => {
                const pos = r.posicao ?? i + 1;
                const dr = formatRankingDelta(r);
                const isOutro = i >= RANKING_GRADIENTS.length;
                const barPct = Math.max(
                  2,
                  ((Number(r.valor_ganho) || 0) / maxValor) * 100
                );
                return (
                  <div
                    key={r.vendedor_id ?? `${r.nome}-${i}`}
                    className={`ranking__row${i < 3 ? ` top-${i + 1}` : ""}`}
                  >
                    <span className="ranking__pos">{pos}</span>
                    <div
                      className="ranking__avatar"
                      style={
                        isOutro
                          ? {
                              background: "var(--surface-2)",
                              color: "var(--ink-2)",
                              border: "1px solid var(--border)",
                            }
                          : { background: RANKING_GRADIENTS[i] }
                      }
                    >
                      {iniciais(r.nome)}
                    </div>
                    <div className="ranking__meta">
                      <div className="nm">{r.nome}</div>
                      <div className="sub">
                        {fmtNumero(r.ganhos, 0)} deals · conv.{" "}
                        {fmtNumero(r.taxa_conversao, 0)}% · ticket R${" "}
                        {fmtCompact(r.ticket_medio)}
                      </div>
                    </div>
                    <div className="ranking__bar" aria-hidden="true">
                      <motion.span
                        style={{
                          background: isOutro
                            ? "var(--ink-4)"
                            : RANKING_GRADIENTS[i],
                        }}
                        initial={animacoes ? { width: 0 } : false}
                        animate={{ width: `${barPct}%` }}
                        transition={trBarra(0.1 + i * 0.05)}
                      />
                    </div>
                    <span className="ranking__val">
                      R$ {fmtCompact(r.valor_ganho)}
                    </span>
                    <span className={`ranking__delta ${dr.tone}`}>
                      {dr.tone === "flat" ? dr.texto : `${ARROW[dr.tone]} ${dr.texto}`}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Ajustes por card (Fase 3) -----------------------------------------------
// Cada card declara `ajustes` com os controles que SUPORTA. O index renderiza
// no SlideOver de config (modo personalizar) apenas os controles aplicaveis:
//   - fonte   : 'pipeline' | 'receita' — grava layout_painel.fontes[fonte];
//               AO SALVAR fonte o index re-fetcha (backend le a fonte da config).
//   - formato : true — moeda/moeda_compacta/percentual + casas -> formatos[id].
//   - grafico : true — line/bar/area + paleta -> charts[id].
// Card sem `ajustes` (ou com tudo false) nao mostra a engrenagem de config.

// --- Catalogo ----------------------------------------------------------------
export const CATALOGO = [
  // 8 KPIs (span pequeno = 3)
  { id: "pipeline", label: "Pipeline aberto", categoria: "kpi", span_default: 3, render: renderPipeline, ajustes: { fonte: "pipeline", formato: true } },
  { id: "receita_realizada", label: "Receita realizada", categoria: "kpi", span_default: 3, render: renderReceitaRealizada, ajustes: { fonte: "receita", formato: true } },
  { id: "taxa_conversao", label: "Taxa conversão", categoria: "kpi", span_default: 3, render: renderTaxaConversao, ajustes: { fonte: "taxa_conversao", grafico: true } },
  { id: "ticket_medio", label: "Ticket médio", categoria: "kpi", span_default: 3, render: renderTicketMedio, ajustes: { fonte: "ticket", fonteKey: "ticket_medio", formato: true, grafico: true } },
  { id: "tempo_resposta", label: "Tempo resposta", categoria: "kpi", span_default: 3, render: renderTempoResposta },
  { id: "tempo_medio_aprovacao", label: "Lead time orç→aprov", categoria: "kpi", span_default: 3, render: renderTempoMedioAprovacao },
  { id: "margem_media", label: "Margem média", categoria: "kpi", span_default: 3, render: renderMargemMedia },
  { id: "aprovacoes_fora_limite", label: "Descontos acima do limite", categoria: "kpi", span_default: 3, render: renderAprovacoesForaLimite },
  // 3 faixas analiticas (span largo). Funil + Perdas eram 60/40 (7+5);
  // ranking era full-width (12). Mantemos esses spans como default.
  { id: "funil", label: "Funil comercial", categoria: "faixa", span_default: 7, render: renderFunil, ajustes: { fonte: "funil" } },
  { id: "por_que_perdemos", label: "Por que perdemos", categoria: "faixa", span_default: 5, render: renderPorQuePerdemos, ajustes: { fonte: "perda" } },
  { id: "ranking_vendedores", label: "Top vendedores", categoria: "faixa", span_default: 12, render: renderRankingVendedores, ajustes: { fonte: "vendedores" } },
  // --- Fase 4 (oculto_default: NAO entram no painel sem config — regressao-zero;
  // o gestor adiciona via "Adicionar card"). KPIs novos + 1 faixa larga.
  // O ponderado e' calculado sobre orcamentos ativos (× probabilidade), nao
  // varia com a fonte do pipeline — entao nao expoe seletor de fonte (so formato).
  { id: "valor_ponderado", label: "Valor ponderado", categoria: "kpi", span_default: 3, render: renderValorPonderado, oculto_default: true, ajustes: { formato: true } },
  { id: "comissao_prevista", label: "Comissão prevista", categoria: "kpi", span_default: 3, render: renderComissaoPrevista, oculto_default: true, ajustes: { formato: true } },
  { id: "leads_sem_orcamento", label: "Leads sem orçamento", categoria: "kpi", span_default: 3, render: renderLeadsSemOrcamento, oculto_default: true },
  { id: "valor_por_etapa", label: "Valor por etapa", categoria: "faixa", span_default: 7, render: renderValorPorEtapa, oculto_default: true },
  // --- Perdas (lead-centrico, oculto_default; fonte perda configuravel) ---
  { id: "valor_perdido", label: "Valor perdido", categoria: "kpi", span_default: 3, render: renderValorPerdido, oculto_default: true, ajustes: { fonte: "perda", formato: true } },
  { id: "taxa_perda", label: "% de perda", categoria: "kpi", span_default: 3, render: renderTaxaPerda, oculto_default: true, ajustes: { fonte: "perda" } },
  { id: "motivos_perda", label: "Motivos de perda", categoria: "faixa", span_default: 5, render: renderMotivosPerda, oculto_default: true, ajustes: { fonte: "perda" } },
];

// Fontes disponiveis por tipo (paridade com o backend FONTES_*_VALIDAS).
// O PADRAO de cada tipo e a opcao recomendada (ADR-0079); a tela mostra
// "Padrão do sistema — <nome>" e lista as demais. `FONTE_PADRAO` (em
// explicacoes.js) espelha os *_FONTE_DEFAULT de comercial/metricas.py.
export const FONTES_POR_TIPO = {
  pipeline: [
    { value: "lead_orcamento_fallback", label: "Lead + orçamento (orçamento do lead ou, sem ele, o valor estimado)" },
    { value: "orcamento", label: "Somente orçamentos" },
    { value: "lead_manual", label: "Valor estimado do lead" },
  ],
  receita: [
    { value: "orcamento_ganho", label: "Orçamento ganho" },
    { value: "pedido_entregue", label: "Pedido entregue" },
    { value: "lead_manual_finalizado", label: "Valor estimado do lead — leads finalizados" },
  ],
  taxa_conversao: [
    { value: "orcamento", label: "Por orçamentos (ganhos ÷ enviados)" },
    { value: "leads", label: "Por leads (finalizados ÷ todos)" },
  ],
  ticket: [
    { value: "orcamento", label: "Orçamentos ganhos" },
    { value: "lead_finalizado", label: "Valor estimado do lead — leads finalizados" },
  ],
  perda: [
    { value: "lead", label: "Por leads (perda registrada pelo vendedor)" },
    { value: "orcamento", label: "Por orçamentos (razões estruturadas)" },
  ],
  vendedores: [
    { value: "lead", label: "Por leads (responsável + ganho)" },
    { value: "orcamento", label: "Por orçamentos (vendedor)" },
  ],
  funil: [
    { value: "lead", label: "Etapas do CRM" },
    { value: "orcamento", label: "Orçamentos do sistema" },
  ],
};

export const CATALOGO_POR_ID = Object.fromEntries(CATALOGO.map((c) => [c.id, c]));

// Ids marcados `oculto_default` — fora do painel ate o gestor adicionar.
export const OCULTO_DEFAULT_IDS = CATALOGO.filter((c) => c.oculto_default).map(
  (c) => c.id
);

// Conjunto (Set) dos ids ocultos por padrao — o index.jsx semeia esses em
// `ocultos` quando nenhuma config os posiciona, garantindo regressao-zero.
export function ocultoDefaultIds() {
  return new Set(OCULTO_DEFAULT_IDS);
}

// Ordem default = ordem do catalogo MENOS os ocultos-por-padrao (Fase 4). Sem
// config, o painel renderiza so os cards visiveis de hoje (regressao-zero); os
// oculto_default ficam disponiveis em "Adicionar card".
export function layoutDefault() {
  return CATALOGO.filter((c) => !c.oculto_default).map((c) => c.id);
}

// Spans permitidos ao redimensionar (ciclar S/M/L). Numeros = grid-column span.
export const SPANS_CICLO = {
  kpi: [3, 6, 12],
  faixa: [4, 7, 12],
};
